/**
 * Domain ranking TABLE columns: catalog, nested aggregation groups,
 * cell-value mapping, and prefs migration.
 *
 * Graph / Overview ranking widgets do not use this module.
 */
import type { ColumnPrefs } from '../../hooks/useReportingColumnPrefs';
import type { DomainStatItem, DomainStatsTotals, DomainType } from '../../types/reporting';
import { INVOICING_GATED_RANKING_PARENT_KEYS } from './shared/invoicingGatedMetrics';

export type DomainTableColumnKey = string;

export type AggregationChild = {
    key: DomainTableColumnKey;
    label: string;
    /** Field on DomainStatItem, or a computed pct-of-total key. */
    itemField?: string;
};

export type AggregationGroup = {
    id: string;
    /** Legacy toggle column key stored in older column prefs. */
    legacyToggleKey: string;
    label: string;
    vendorLabel?: string;
    children: AggregationChild[];
    defaultChildKey: DomainTableColumnKey;
};

export type LegacyToggleMetrics = {
    gmMetric?: 'avg' | 'median';
    missedOppMetric?: 'avg' | 'median';
    pctTotalMetric?: 'volume' | 'count' | 'profit';
    orderSizeMetric?: 'avg' | 'median';
    discountMetric?: 'avg' | 'median';
    discountWoPMMetric?: 'avg' | 'median';
    gmWoPMMetric?: 'avg' | 'median';
    partValueMetric?: 'avg' | 'median';
    etaChangeMetric?: 'avg' | 'median';
    costVarianceMetric?: 'total' | 'avg' | 'median';
    deliveryVarianceMetric?: 'avg' | 'median';
    pctOemMetric?: 'volume' | 'count';
    pctLkqMetric?: 'volume' | 'count';
    pctAftermarketMetric?: 'volume' | 'count';
};

export type ColumnPickerChild = {
    key: DomainTableColumnKey;
    label: string;
    visible: boolean;
};

export type ColumnPickerRow = {
    key: string;
    label: string;
    visible: boolean;
    pinned: boolean;
    children?: ColumnPickerChild[];
};

export type ColumnDisplayConfig = {
    label: string;
    format: (v: number) => string;
    type: 'percent' | 'number';
    isNegative?: boolean;
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
const formatCurrencyPrecise = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;
const formatNumber = (value: number) => (value || 0).toLocaleString();
const formatDays = (value: number) => `${value > 0 ? '+' : ''}${(value || 0).toFixed(1)}d`;
const formatQuotesPerOrder = (v: number) => (v || 0).toFixed(1);

/** Distinct leaf keys for vendor Discount columns (same numbers as Gross Margin). */
export const DISCOUNT_AVG_KEY = 'discountAvg';
export const DISCOUNT_MEDIAN_KEY = 'discountMedian';
export const DISCOUNT_WO_PM_AVG_KEY = 'discountWoPMAvg';
export const DISCOUNT_WO_PM_MEDIAN_KEY = 'discountWoPMMedian';

export const AGGREGATION_GROUPS: AggregationGroup[] = [
    {
        id: 'orderSize',
        legacyToggleKey: 'orderSizeToggle',
        label: 'Order Size',
        children: [
            { key: 'avgOrderSize', label: 'Avg.', itemField: 'avgOrderSize' },
            { key: 'medianOrderSize', label: 'Median', itemField: 'medianOrderSize' },
        ],
        defaultChildKey: 'avgOrderSize',
    },
    {
        id: 'grossMargin',
        legacyToggleKey: 'grossMarginToggle',
        label: 'Gross Margin',
        vendorLabel: 'Discount',
        children: [
            { key: 'avgGrossMarginPct', label: 'Avg.', itemField: 'avgGrossMarginPct' },
            { key: 'medianGrossMarginPct', label: 'Median', itemField: 'medianGrossMarginPct' },
        ],
        defaultChildKey: 'avgGrossMarginPct',
    },
    {
        id: 'grossMarginWoPM',
        legacyToggleKey: 'grossMarginWoPMToggle',
        label: 'Gross Margin (w/o PM)',
        vendorLabel: 'Discount (w/o PM)',
        children: [
            { key: 'avgGrossMarginPctWoPM', label: 'Avg.', itemField: 'avgGrossMarginPctWoPM' },
            { key: 'medianGrossMarginPctWoPM', label: 'Median', itemField: 'medianGrossMarginPctWoPM' },
        ],
        defaultChildKey: 'avgGrossMarginPctWoPM',
    },
    {
        id: 'missedOpp',
        legacyToggleKey: 'missedOppToggle',
        label: 'Missed Opportunity',
        children: [
            { key: 'missedOpportunity', label: 'Total', itemField: 'missedOpportunity' },
            { key: 'avgMissedOpportunity', label: 'Avg.', itemField: 'avgMissedOpportunity' },
            { key: 'medianMissedOpportunity', label: 'Median', itemField: 'medianMissedOpportunity' },
        ],
        defaultChildKey: 'avgMissedOpportunity',
    },
    {
        id: 'pctTotal',
        legacyToggleKey: 'pctTotalToggle',
        label: '% of Total',
        children: [
            { key: 'pctOfTotalVolume', label: 'Volume' },
            { key: 'pctOfTotalCount', label: 'Count' },
            { key: 'pctOfTotalProfit', label: 'Profit' },
        ],
        defaultChildKey: 'pctOfTotalVolume',
    },
    {
        id: 'discount',
        legacyToggleKey: 'discountToggle',
        label: 'Discount',
        children: [
            { key: DISCOUNT_AVG_KEY, label: 'Avg.', itemField: 'avgGrossMarginPct' },
            { key: DISCOUNT_MEDIAN_KEY, label: 'Median', itemField: 'medianGrossMarginPct' },
        ],
        defaultChildKey: DISCOUNT_AVG_KEY,
    },
    {
        id: 'discountWoPM',
        legacyToggleKey: 'discountWoPMToggle',
        label: 'Discount (w/o PM)',
        children: [
            { key: DISCOUNT_WO_PM_AVG_KEY, label: 'Avg.', itemField: 'avgGrossMarginPctWoPM' },
            { key: DISCOUNT_WO_PM_MEDIAN_KEY, label: 'Median', itemField: 'medianGrossMarginPctWoPM' },
        ],
        defaultChildKey: DISCOUNT_WO_PM_AVG_KEY,
    },
    {
        id: 'partValue',
        legacyToggleKey: 'partValueToggle',
        label: 'Part Value',
        children: [
            { key: 'avgPartValue', label: 'Avg.', itemField: 'avgPartValue' },
            { key: 'medianPartValue', label: 'Median', itemField: 'medianPartValue' },
        ],
        defaultChildKey: 'avgPartValue',
    },
    {
        id: 'etaChange',
        legacyToggleKey: 'etaChangeToggle',
        label: 'ETA Change',
        children: [
            { key: 'avgEtaChangeDays', label: 'Avg.', itemField: 'avgEtaChangeDays' },
            { key: 'medianEtaChangeDays', label: 'Median', itemField: 'medianEtaChangeDays' },
        ],
        defaultChildKey: 'avgEtaChangeDays',
    },
    {
        id: 'costVariance',
        legacyToggleKey: 'costVarianceToggle',
        label: 'Cost Variance',
        children: [
            { key: 'totalCostVariance', label: 'Total', itemField: 'totalCostVariance' },
            { key: 'avgCostVariance', label: 'Avg.', itemField: 'avgCostVariance' },
            { key: 'medianCostVariance', label: 'Median', itemField: 'medianCostVariance' },
        ],
        defaultChildKey: 'avgCostVariance',
    },
    {
        id: 'deliveryVariance',
        legacyToggleKey: 'deliveryVarianceToggle',
        label: 'Delivery Variance',
        children: [
            { key: 'avgDeliveryVarianceDays', label: 'Avg.', itemField: 'avgDeliveryVarianceDays' },
            { key: 'medianDeliveryVarianceDays', label: 'Median', itemField: 'medianDeliveryVarianceDays' },
        ],
        defaultChildKey: 'avgDeliveryVarianceDays',
    },
    {
        id: 'pctOem',
        legacyToggleKey: 'pctOemToggle',
        label: '% OEM',
        children: [
            { key: 'pctOemVolume', label: 'Volume', itemField: 'pctOemVolume' },
            { key: 'pctOemCount', label: 'Count', itemField: 'pctOemCount' },
        ],
        defaultChildKey: 'pctOemVolume',
    },
    {
        id: 'pctLkq',
        legacyToggleKey: 'pctLkqToggle',
        label: '% LKQ',
        children: [
            { key: 'pctLkqVolume', label: 'Volume', itemField: 'pctLkqVolume' },
            { key: 'pctLkqCount', label: 'Count', itemField: 'pctLkqCount' },
        ],
        defaultChildKey: 'pctLkqVolume',
    },
    {
        id: 'pctAftermarket',
        legacyToggleKey: 'pctAftermarketToggle',
        label: '% Aftermarket',
        children: [
            { key: 'pctAftermarketVolume', label: 'Volume', itemField: 'pctAftermarketVolume' },
            { key: 'pctAftermarketCount', label: 'Count', itemField: 'pctAftermarketCount' },
        ],
        defaultChildKey: 'pctAftermarketVolume',
    },
];

const GROUP_BY_ID = new Map(AGGREGATION_GROUPS.map((g) => [g.id, g]));
const GROUP_BY_LEGACY = new Map(AGGREGATION_GROUPS.map((g) => [g.legacyToggleKey, g]));
const GROUP_BY_CHILD = new Map<string, AggregationGroup>();
AGGREGATION_GROUPS.forEach((g) => {
    g.children.forEach((c) => GROUP_BY_CHILD.set(c.key, g));
});

const LEGACY_METRIC_TO_CHILD: Record<string, (m: LegacyToggleMetrics) => DomainTableColumnKey> = {
    orderSizeToggle: (m) => (m.orderSizeMetric === 'median' ? 'medianOrderSize' : 'avgOrderSize'),
    grossMarginToggle: (m) => (m.gmMetric === 'median' ? 'medianGrossMarginPct' : 'avgGrossMarginPct'),
    grossMarginWoPMToggle: (m) => (m.gmWoPMMetric === 'median' ? 'medianGrossMarginPctWoPM' : 'avgGrossMarginPctWoPM'),
    missedOppToggle: (m) => (m.missedOppMetric === 'median' ? 'medianMissedOpportunity' : 'avgMissedOpportunity'),
    pctTotalToggle: (m) => (
        m.pctTotalMetric === 'count' ? 'pctOfTotalCount'
            : m.pctTotalMetric === 'profit' ? 'pctOfTotalProfit'
                : 'pctOfTotalVolume'
    ),
    discountToggle: (m) => (m.discountMetric === 'median' ? DISCOUNT_MEDIAN_KEY : DISCOUNT_AVG_KEY),
    discountWoPMToggle: (m) => (m.discountWoPMMetric === 'median' ? DISCOUNT_WO_PM_MEDIAN_KEY : DISCOUNT_WO_PM_AVG_KEY),
    partValueToggle: (m) => (m.partValueMetric === 'median' ? 'medianPartValue' : 'avgPartValue'),
    etaChangeToggle: (m) => (m.etaChangeMetric === 'median' ? 'medianEtaChangeDays' : 'avgEtaChangeDays'),
    costVarianceToggle: (m) => (
        m.costVarianceMetric === 'median' ? 'medianCostVariance'
            : m.costVarianceMetric === 'total' ? 'totalCostVariance'
                : 'avgCostVariance'
    ),
    deliveryVarianceToggle: (m) => (m.deliveryVarianceMetric === 'median' ? 'medianDeliveryVarianceDays' : 'avgDeliveryVarianceDays'),
    pctOemToggle: (m) => (m.pctOemMetric === 'count' ? 'pctOemCount' : 'pctOemVolume'),
    pctLkqToggle: (m) => (m.pctLkqMetric === 'count' ? 'pctLkqCount' : 'pctLkqVolume'),
    pctAftermarketToggle: (m) => (m.pctAftermarketMetric === 'count' ? 'pctAftermarketCount' : 'pctAftermarketVolume'),
};

/** Catalog order matches the previous ALL_COLUMNS (toggle keys still in this list). */
const ALL_PARENT_KEYS = [
    'orderCount', 'orderVolume', 'partsOrdered', 'grossProfit',
    'orderSizeToggle',
    'grossMarginToggle', 'grossMarginWoPMToggle', 'grossMarginLossFromPM',
    'missedOppToggle',
    'partListPricePctOfOE',
    'pctTotalToggle',
    'p90GrossMarginPct',
    'agreedDiscount', 'discountToggle', 'discountWoPMToggle',
    'pctAutoOrdered',
    'partValueToggle', 'etaChangeToggle',
    'costVarianceToggle', 'deliveryVarianceToggle', 'pctPartsInvoiced',
    'quoteRate', 'quoteOpportunities', 'quotesSubmitted',
    'totalQuotes', 'avgQuotesPerOrder',
    'totalReturnCount', 'totalReturnValue', 'totalReturnPct',
    'pctOemToggle', 'pctLkqToggle', 'pctAftermarketToggle',
    'pctPartsFlagged',
] as const;

const VENDOR_ONLY = [
    'agreedDiscount', 'discountToggle', 'discountWoPMToggle', 'etaChangeToggle',
    'costVarianceToggle', 'deliveryVarianceToggle',
    'quoteRate', 'quoteOpportunities', 'quotesSubmitted',
];
const AUTOMATION_COLS = ['pctAutoOrdered'];
const LOCATION_ONLY = ['totalQuotes', 'avgQuotesPerOrder', 'pctOemToggle', 'pctLkqToggle', 'pctAftermarketToggle'];
const OEM_ONLY = ['p90GrossMarginPct', 'pctPartsFlagged'];

const PARENT_KEYS_BY_DOMAIN: Record<DomainType, string[]> = {
    vendor: ALL_PARENT_KEYS.filter((c) => !LOCATION_ONLY.includes(c) && !OEM_ONLY.includes(c)),
    location: ALL_PARENT_KEYS.filter((c) => !VENDOR_ONLY.includes(c)),
    insurance: ALL_PARENT_KEYS.filter((c) => !VENDOR_ONLY.includes(c) && !AUTOMATION_COLS.includes(c) && !OEM_ONLY.includes(c)),
    oem: ALL_PARENT_KEYS.filter((c) => !VENDOR_ONLY.includes(c) && !AUTOMATION_COLS.includes(c)),
};

/** Previous default-visible toggle/leaf keys per domain (before nested children). */
export const LEGACY_DEFAULT_VISIBLE: Record<DomainType, string[]> = {
    vendor: [
        'orderVolume', 'orderCount', 'partsOrdered', 'orderSizeToggle',
        'agreedDiscount', 'discountToggle', 'discountWoPMToggle', 'etaChangeToggle',
        'costVarianceToggle', 'deliveryVarianceToggle', 'pctPartsInvoiced', 'quoteRate',
        'pctTotalToggle',
    ],
    location: ['orderCount', 'orderVolume', 'grossMarginToggle', 'grossMarginWoPMToggle', 'missedOppToggle', 'partListPricePctOfOE', 'pctTotalToggle'],
    insurance: ['orderVolume', 'orderCount', 'partsOrdered', 'grossMarginToggle', 'grossMarginWoPMToggle', 'missedOppToggle', 'partListPricePctOfOE', 'pctTotalToggle'],
    oem: ['orderCount', 'partsOrdered', 'orderVolume', 'grossMarginToggle', 'grossMarginWoPMToggle', 'missedOppToggle', 'partListPricePctOfOE', 'pctTotalToggle'],
};

export const COLUMN_CONFIG: Record<string, ColumnDisplayConfig> = {
    orderCount: { label: 'Order Count', format: formatNumber, type: 'number' },
    orderVolume: { label: 'Order Volume', format: formatCurrency, type: 'number' },
    partsOrdered: { label: 'Part Count', format: formatNumber, type: 'number' },
    grossProfit: { label: 'Gross Profit', format: formatCurrency, type: 'number' },
    avgGrossMarginPct: { label: 'Avg GM %', format: formatPercent, type: 'percent' },
    medianGrossMarginPct: { label: 'Median GM %', format: formatPercent, type: 'percent' },
    avgGrossMarginPctWoPM: { label: 'Avg GM % (w/o PM)', format: formatPercent, type: 'percent' },
    medianGrossMarginPctWoPM: { label: 'Median GM % (w/o PM)', format: formatPercent, type: 'percent' },
    grossMarginLossFromPM: { label: 'GM Loss PM', format: formatPercent, type: 'percent', isNegative: true },
    missedOpportunity: { label: 'Total Missed Opportunity', format: formatCurrency, type: 'number', isNegative: true },
    avgMissedOpportunity: { label: 'Avg. Missed Opportunity', format: formatCurrency, type: 'number', isNegative: true },
    medianMissedOpportunity: { label: 'Median Missed Opportunity', format: formatCurrency, type: 'number', isNegative: true },
    pctAutoOrdered: { label: '% Auto Orders Fully Ordered', format: formatPercent, type: 'percent' },
    partListPricePctOfOE: { label: 'Part List Price % of OE', format: formatPercent, type: 'percent' },
    pctOfTotalVolume: { label: '% of Total (Volume)', format: formatPercent, type: 'percent' },
    pctOfTotalCount: { label: '% of Total (Count)', format: formatPercent, type: 'percent' },
    pctOfTotalProfit: { label: '% of Total (Profit)', format: formatPercent, type: 'percent' },
    avgOrderSize: { label: 'Avg. Order Size', format: formatCurrency, type: 'number' },
    medianOrderSize: { label: 'Median Order Size', format: formatCurrency, type: 'number' },
    agreedDiscount: { label: 'Agreed Discount', format: formatPercent, type: 'percent' },
    [DISCOUNT_AVG_KEY]: { label: 'Avg. Discount', format: formatPercent, type: 'percent' },
    [DISCOUNT_MEDIAN_KEY]: { label: 'Median Discount', format: formatPercent, type: 'percent' },
    [DISCOUNT_WO_PM_AVG_KEY]: { label: 'Avg. Discount (w/o PM)', format: formatPercent, type: 'percent' },
    [DISCOUNT_WO_PM_MEDIAN_KEY]: { label: 'Median Discount (w/o PM)', format: formatPercent, type: 'percent' },
    p90GrossMarginPct: { label: 'Suggested Thresholds', format: formatPercent, type: 'percent' },
    avgPartValue: { label: 'Avg. Part Value', format: formatCurrency, type: 'number' },
    medianPartValue: { label: 'Median Part Value', format: formatCurrency, type: 'number' },
    avgEtaChangeDays: { label: 'Avg. ETA Change', format: formatDays, type: 'number', isNegative: true },
    medianEtaChangeDays: { label: 'Median ETA Change', format: formatDays, type: 'number', isNegative: true },
    totalCostVariance: { label: 'Total Cost Variance', format: formatCurrencyPrecise, type: 'number', isNegative: true },
    avgCostVariance: { label: 'Avg. Cost Variance', format: formatCurrencyPrecise, type: 'number', isNegative: true },
    medianCostVariance: { label: 'Median Cost Variance', format: formatCurrencyPrecise, type: 'number', isNegative: true },
    avgDeliveryVarianceDays: { label: 'Avg. Delivery Variance', format: formatDays, type: 'number', isNegative: true },
    medianDeliveryVarianceDays: { label: 'Median Delivery Variance', format: formatDays, type: 'number', isNegative: true },
    pctPartsInvoiced: { label: '% Parts Invoiced', format: formatPercent, type: 'percent' },
    quoteRate: { label: 'Quote Rate', format: formatPercent, type: 'percent' },
    quoteOpportunities: { label: 'Quote Opportunities', format: formatNumber, type: 'number' },
    quotesSubmitted: { label: 'Quotes Submitted', format: formatNumber, type: 'number' },
    totalQuotes: { label: 'Total Quotes', format: formatNumber, type: 'number' },
    avgQuotesPerOrder: { label: 'Quotes/Order', format: formatQuotesPerOrder, type: 'number' },
    totalReturnCount: { label: 'Return Count', format: formatNumber, type: 'number', isNegative: true },
    totalReturnValue: { label: 'Return Value', format: formatCurrency, type: 'number', isNegative: true },
    totalReturnPct: { label: 'Return %', format: formatPercent, type: 'percent', isNegative: true },
    pctOemVolume: { label: '% OEM (Volume)', format: formatPercent, type: 'percent' },
    pctOemCount: { label: '% OEM (Count)', format: formatPercent, type: 'percent' },
    pctLkqVolume: { label: '% LKQ (Volume)', format: formatPercent, type: 'percent' },
    pctLkqCount: { label: '% LKQ (Count)', format: formatPercent, type: 'percent' },
    pctAftermarketVolume: { label: '% Aftermarket (Volume)', format: formatPercent, type: 'percent' },
    pctAftermarketCount: { label: '% Aftermarket (Count)', format: formatPercent, type: 'percent' },
    pctPartsFlagged: { label: '% Parts Flagged', format: formatPercent, type: 'percent' },
};

const MO_KEYS = new Set(['missedOpportunity', 'avgMissedOpportunity', 'medianMissedOpportunity']);
const PCT_TOTAL_KEYS = new Set(['pctOfTotalVolume', 'pctOfTotalCount', 'pctOfTotalProfit']);

function expandParentKey(parentKey: string): DomainTableColumnKey[] {
    const group = GROUP_BY_LEGACY.get(parentKey);
    if (!group) return [parentKey];
    return group.children.map((c) => c.key);
}

export function getGroupForKey(key: string): AggregationGroup | undefined {
    return GROUP_BY_ID.get(key) || GROUP_BY_LEGACY.get(key) || GROUP_BY_CHILD.get(key);
}

export function isLegacyToggleKey(key: string): boolean {
    return GROUP_BY_LEGACY.has(key);
}

export type DomainColumnOptions = {
    invoicingEnabled?: boolean;
};

export function getLeafKeysForDomain(
    domain: DomainType,
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): DomainTableColumnKey[] {
    const invoicingEnabled = options.invoicingEnabled !== false;
    let parents = PARENT_KEYS_BY_DOMAIN[domain] ?? PARENT_KEYS_BY_DOMAIN.location;
    if (!invoicingEnabled) {
        parents = parents.filter((k) => !INVOICING_GATED_RANKING_PARENT_KEYS.has(k));
    }
    if (domain === 'vendor' && vendorGroupBy === 'preference') {
        parents = parents.filter((k) =>
            k !== 'agreedDiscount'
            && k !== 'quoteRate'
            && k !== 'quoteOpportunities'
            && k !== 'quotesSubmitted'
        );
    }
    return parents.flatMap(expandParentKey);
}

export function getDefaultVisibleLeafKeys(
    domain: DomainType,
    metrics: LegacyToggleMetrics = {},
    options: DomainColumnOptions = {},
): DomainTableColumnKey[] {
    const visibleParents = new Set(LEGACY_DEFAULT_VISIBLE[domain]);
    return getLeafKeysForDomain(domain, undefined, options).filter((leaf) => {
        const group = GROUP_BY_CHILD.get(leaf);
        if (group) {
            return visibleParents.has(group.legacyToggleKey) && leaf === LEGACY_METRIC_TO_CHILD[group.legacyToggleKey](metrics);
        }
        return visibleParents.has(leaf);
    });
}

export function getDefaultHiddenLeafKeys(
    domain: DomainType,
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): DomainTableColumnKey[] {
    const visible = new Set(getDefaultVisibleLeafKeys(domain, {}, options));
    return getLeafKeysForDomain(domain, vendorGroupBy, options).filter((k) => !visible.has(k));
}

export function groupLabel(group: AggregationGroup, domain: DomainType): string {
    return domain === 'vendor' && group.vendorLabel ? group.vendorLabel : group.label;
}

export function pickerParentLabel(group: AggregationGroup, domain: DomainType, visibleChildKeys: string[]): string {
    const base = groupLabel(group, domain);
    const labels = group.children
        .filter((c) => visibleChildKeys.includes(c.key))
        .map((c) => c.label.replace(/\.$/, ''));
    if (labels.length === 0) return base;
    return `${base} (${labels.join(', ')})`;
}

export function getColumnLabel(key: string, domain: DomainType = 'location'): string {
    const group = GROUP_BY_CHILD.get(key);
    if (group) {
        const child = group.children.find((c) => c.key === key);
        const base = groupLabel(group, domain);
        if (child) return `${base} (${child.label.replace(/\.$/, '')})`;
    }
    return COLUMN_CONFIG[key]?.label || key;
}

function itemFieldForColumn(col: string): string | undefined {
    const group = GROUP_BY_CHILD.get(col);
    const child = group?.children.find((c) => c.key === col);
    return child?.itemField ?? (COLUMN_CONFIG[col] && !PCT_TOTAL_KEYS.has(col) ? col : undefined);
}

export function getChangeMapKey(col: string): string | null {
    if (PCT_TOTAL_KEYS.has(col) || col === 'agreedDiscount') return null;
    return itemFieldForColumn(col) ?? col;
}

export function getTableCellValue(
    item: DomainStatItem | DomainStatsTotals,
    col: string,
    totals: DomainStatsTotals,
): number | null {
    if (MO_KEYS.has(col) && 'missedOpportunityEligible' in item && item.missedOpportunityEligible === false) {
        return null;
    }

    if (col === 'agreedDiscount') {
        return 'agreedDiscount' in item ? item.agreedDiscount ?? null : null;
    }

    if (PCT_TOTAL_KEYS.has(col)) {
        if (item === totals) return 100;
        let numerator = 0;
        let denominator = 0;
        if (col === 'pctOfTotalVolume') {
            numerator = item.orderVolume;
            denominator = totals.orderVolume;
        } else if (col === 'pctOfTotalCount') {
            numerator = item.orderCount;
            denominator = totals.orderCount;
        } else {
            numerator = item.grossProfit;
            denominator = totals.grossProfit;
        }
        return denominator ? (numerator / denominator) * 100 : 0;
    }

    const field = itemFieldForColumn(col) ?? col;
    const value = (item as unknown as Record<string, unknown>)[field];
    return typeof value === 'number' ? value : null;
}

export function migrateLegacyColumnKey(key: string, metrics: LegacyToggleMetrics = {}): string {
    const pick = LEGACY_METRIC_TO_CHILD[key];
    return pick ? pick(metrics) : key;
}

/** Resolve a legacy toggle + metric state to the data field used in the table. */
export function resolveLegacyToggleField(toggleKey: string, metrics: LegacyToggleMetrics = {}): string {
    const leaf = migrateLegacyColumnKey(toggleKey, metrics);
    return itemFieldForColumn(leaf) ?? leaf;
}

export function migrateColumnPrefs(
    prefs: ColumnPrefs,
    domain: DomainType,
    metrics: LegacyToggleMetrics = {},
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): { prefs: ColumnPrefs; changed: boolean } {
    const allKeys = getLeafKeysForDomain(domain, vendorGroupBy, options);
    const allSet = new Set(allKeys);
    const defaultHidden = new Set(getDefaultHiddenLeafKeys(domain, vendorGroupBy, options));

    const order: string[] = [];
    const hidden = new Set<string>();
    let changed = false;
    const seen = new Set<string>();

    const pushLeaf = (key: string, isHidden: boolean) => {
        if (!allSet.has(key) || seen.has(key)) return;
        order.push(key);
        seen.add(key);
        if (isHidden) hidden.add(key);
    };

    for (const key of prefs.order) {
        const group = GROUP_BY_LEGACY.get(key);
        const wasHidden = prefs.hidden.includes(key);
        if (group) {
            changed = true;
            const selected = LEGACY_METRIC_TO_CHILD[key](metrics);
            for (const child of group.children) {
                pushLeaf(child.key, wasHidden || child.key !== selected);
            }
            continue;
        }
        if (GROUP_BY_CHILD.has(key) || allSet.has(key)) {
            pushLeaf(key, prefs.hidden.includes(key));
        } else {
            changed = true;
        }
    }

    for (const key of allKeys) {
        if (seen.has(key)) continue;
        changed = true;
        pushLeaf(key, defaultHidden.has(key));
    }

    const nextHidden = Array.from(hidden);
    const sameOrder = prefs.order.length === order.length && prefs.order.every((k, i) => k === order[i]);
    const sameHidden = prefs.hidden.length === nextHidden.length && prefs.hidden.every((k) => hidden.has(k));
    if (!changed && sameOrder && sameHidden) {
        return { prefs, changed: false };
    }

    return { prefs: { order, hidden: nextHidden }, changed: true };
}

export function migrateColumnPrefsInStorage(
    storageKey: string,
    domain: DomainType,
    metrics: LegacyToggleMetrics = {},
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): void {
    if (!storageKey || typeof localStorage === 'undefined') return;
    try {
        const raw = localStorage.getItem(storageKey);
        const fallback: ColumnPrefs = {
            order: getLeafKeysForDomain(domain, vendorGroupBy, options),
            hidden: getDefaultHiddenLeafKeys(domain, vendorGroupBy, options),
        };
        const current: ColumnPrefs = raw ? JSON.parse(raw) : fallback;
        if (!current || !Array.isArray(current.order)) return;
        const { prefs, changed } = migrateColumnPrefs(current, domain, metrics, vendorGroupBy, options);
        if (changed) {
            localStorage.setItem(storageKey, JSON.stringify(prefs));
        }
    } catch {
        // ignore
    }
}

export function prefsToPickerRows(
    prefs: ColumnPrefs,
    domain: DomainType,
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): ColumnPickerRow[] {
    const allKeys = getLeafKeysForDomain(domain, vendorGroupBy, options);
    const hidden = new Set(prefs.hidden);
    const emittedGroups = new Set<string>();
    const rows: ColumnPickerRow[] = [];

    const childOrderIndex = (key: string) => {
        const i = prefs.order.indexOf(key);
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };

    for (const key of prefs.order.filter((k) => allKeys.includes(k))) {
        const group = GROUP_BY_CHILD.get(key);
        if (!group) {
            rows.push({
                key,
                label: getColumnLabel(key, domain),
                visible: !hidden.has(key),
                pinned: false,
            });
            continue;
        }
        if (emittedGroups.has(group.id)) continue;
        emittedGroups.add(group.id);
        const children = [...group.children]
            .sort((a, b) => childOrderIndex(a.key) - childOrderIndex(b.key))
            .map((c) => ({
                key: c.key,
                label: c.label,
                visible: !hidden.has(c.key) && prefs.order.includes(c.key),
            }));
        const visibleChildKeys = children.filter((c) => c.visible).map((c) => c.key);
        rows.push({
            key: group.id,
            label: pickerParentLabel(group, domain, visibleChildKeys),
            visible: visibleChildKeys.length > 0,
            pinned: false,
            children,
        });
    }

    for (const key of allKeys) {
        const group = GROUP_BY_CHILD.get(key);
        if (group) {
            if (emittedGroups.has(group.id)) continue;
            emittedGroups.add(group.id);
            const children = group.children.map((c) => ({
                key: c.key,
                label: c.label,
                visible: false,
            }));
            rows.push({
                key: group.id,
                label: groupLabel(group, domain),
                visible: false,
                pinned: false,
                children,
            });
        } else if (!prefs.order.includes(key)) {
            rows.push({
                key,
                label: getColumnLabel(key, domain),
                visible: false,
                pinned: false,
            });
        }
    }

    return rows;
}

export function pickerRowsToPrefs(
    rows: ColumnPickerRow[],
    domain: DomainType,
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): ColumnPrefs {
    const allKeys = getLeafKeysForDomain(domain, vendorGroupBy, options);
    const order: string[] = [];
    const hidden: string[] = [];
    const seen = new Set<string>();

    for (const row of rows) {
        if (row.children && row.children.length > 0) {
            for (const child of row.children) {
                if (seen.has(child.key)) continue;
                order.push(child.key);
                seen.add(child.key);
                if (!child.visible) hidden.push(child.key);
            }
            continue;
        }
        if (seen.has(row.key) || GROUP_BY_ID.has(row.key)) continue;
        order.push(row.key);
        seen.add(row.key);
        if (!row.visible) hidden.push(row.key);
    }

    for (const key of allKeys) {
        if (seen.has(key)) continue;
        order.push(key);
        hidden.push(key);
    }

    return { order, hidden };
}

export function togglePickerRowForDomain(
    rows: ColumnPickerRow[],
    key: string,
    domain: DomainType,
): ColumnPickerRow[] {
    return rows.map((row) => {
        if (row.key === key && row.children) {
            const anyOn = row.children.some((c) => c.visible);
            const group = GROUP_BY_ID.get(row.key);
            if (anyOn) {
                return {
                    ...row,
                    visible: false,
                    label: group ? groupLabel(group, domain) : row.label,
                    children: row.children.map((c) => ({ ...c, visible: false })),
                };
            }
            const defaultKey = group?.defaultChildKey;
            const children = row.children.map((c) => ({ ...c, visible: c.key === defaultKey }));
            return {
                ...row,
                visible: true,
                label: group ? pickerParentLabel(group, domain, children.filter((c) => c.visible).map((c) => c.key)) : row.label,
                children,
            };
        }
        if (row.key === key && !row.children) {
            return { ...row, visible: !row.visible };
        }
        if (row.children?.some((c) => c.key === key)) {
            const children = row.children.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c));
            const visibleChildKeys = children.filter((c) => c.visible).map((c) => c.key);
            const group = GROUP_BY_ID.get(row.key);
            return {
                ...row,
                visible: visibleChildKeys.length > 0,
                label: group ? pickerParentLabel(group, domain, visibleChildKeys) : row.label,
                children,
            };
        }
        return row;
    });
}

export function reorderPickerRows(rows: ColumnPickerRow[], fromKey: string, toKey: string): ColumnPickerRow[] {
    if (fromKey === toKey) return rows;

    const parentOfChild = (key: string) => rows.find((r) => r.children?.some((c) => c.key === key));

    const fromParent = parentOfChild(fromKey);
    const toParent = parentOfChild(toKey);

    // Child reorder: only within the same group.
    if (fromParent && toParent && fromParent.key === toParent.key && fromParent.children) {
        const children = [...fromParent.children];
        const fi = children.findIndex((c) => c.key === fromKey);
        const ti = children.findIndex((c) => c.key === toKey);
        if (fi < 0 || ti < 0) return rows;
        const [item] = children.splice(fi, 1);
        children.splice(ti, 0, item);
        return rows.map((r) => (r.key === fromParent.key ? { ...r, children } : r));
    }

    // Do not allow dragging a child onto another group or a leaf.
    if (fromParent || toParent) return rows;

    const fromIdx = rows.findIndex((r) => r.key === fromKey);
    const toIdx = rows.findIndex((r) => r.key === toKey);
    if (fromIdx < 0 || toIdx < 0) return rows;
    if (rows[fromIdx].pinned || rows[toIdx].pinned) return rows;
    const next = [...rows];
    const [item] = next.splice(fromIdx, 1);
    const newToIdx = next.findIndex((r) => r.key === toKey);
    if (newToIdx < 0) return rows;
    next.splice(newToIdx, 0, item);
    return next;
}

export function defaultPickerRows(
    domain: DomainType,
    vendorGroupBy?: 'vendor' | 'preference',
    options: DomainColumnOptions = {},
): ColumnPickerRow[] {
    const prefs: ColumnPrefs = {
        order: getLeafKeysForDomain(domain, vendorGroupBy, options),
        hidden: getDefaultHiddenLeafKeys(domain, vendorGroupBy, options),
    };
    return prefsToPickerRows(prefs, domain, vendorGroupBy, options);
}

export function visibleLeafKeysFromPicker(rows: ColumnPickerRow[]): DomainTableColumnKey[] {
    const keys: string[] = [];
    for (const row of rows) {
        if (row.children) {
            for (const child of row.children) {
                if (child.visible) keys.push(child.key);
            }
        } else if (row.visible) {
            keys.push(row.key);
        }
    }
    return keys;
}

/** Data fields currently shown for default table columns (legacy toggle + default avg/volume). */
export function defaultVisibleDataFields(domain: DomainType): string[] {
    return getDefaultVisibleLeafKeys(domain).map((leaf) => itemFieldForColumn(leaf) ?? leaf);
}
